import Head from 'next/head';

import {
  CountryCode,
  DisplayLanguage,
  ROBOTS_CONFIG
} from 'src/utils/constants/stringConstants';

import { assetsForNoIndex } from '../../../utils/constants/utilConstants';
import { filterPathname } from '../SEOURLUtils';
import SimpleStore from '../../../store/SimpleStore';

interface CommonSEOMetaProps {
  displayLanguage: string;
  pathName: string;
  countryCode: string;
  isBotRequest: boolean;
  appHostname?: string | undefined;
}

const getNoIndexNoFollowFlag = (pathName: string, contentVersion?: string) => {
  const webSeriesRegex = /(web-series)/i;
  let noIndexNoFollowFlag = !!(
    webSeriesRegex.test(pathName) &&
    contentVersion &&
    contentVersion !== 'Original'
  );

  if (!noIndexNoFollowFlag) {
    const noIndexNoFollowPagesRegex =
      /^\/(myprofile|settings|parentalcontrol|referral|ref)/gi;
    if (noIndexNoFollowPagesRegex.test(pathName)) {
      noIndexNoFollowFlag = true;
    }
  }
  return noIndexNoFollowFlag;
};

const getNoIndexFlag = (country: string, pathName: string) => {
  const noIndexPagesRegex = /^\/(paymentsuccess|premium)/i;
  return country === CountryCode.IN
    ? noIndexPagesRegex.test(pathName) || assetsForNoIndex.test(pathName)
    : false;
};

const getDisplayLanguageForMeta = (
  country: string,
  displayLanguage: string,
  isBot: boolean
) => {
  if (!isBot) return DisplayLanguage.ENGLISH;
  return country === CountryCode.IN
    ? `${displayLanguage}-${CountryCode.IN}`
    : displayLanguage;
};

const CommonSEOMeta = (props: CommonSEOMetaProps) => {
  const { displayLanguage, pathName, countryCode, isBotRequest, appHostname } =
    props;

  const robotConfig = SimpleStore.get(ROBOTS_CONFIG) || {};

  const {
    noIndexNoFollow = false,
    noIndex = false,
    noFollow = false,
    contentVersion = ''
  } = robotConfig;

  // These collection IDs are to required to be folowed by bots
  const collectionAssetsForIndexFollow =
    /^(0-8-6633|0-8-3z5206534|0-8-6672)$/gi;

  const pathNameFiltered = filterPathname(pathName);
  const showDetailsRegex = /^\/(tv-shows)\/(details)/g;
  const movieDetailsRegex = /^\/movies\/details\//gi;

  const pathNameArray = pathNameFiltered.split('/');
  const isShowDetail = showDetailsRegex.test(pathNameFiltered);
  const isMovieDetails =
    !noIndexNoFollow &&
    movieDetailsRegex.test(pathNameFiltered) &&
    countryCode == CountryCode.IN &&
    !assetsForNoIndex.test(pathNameFiltered);
  const isShowsCollection =
    (pathNameArray[1] === 'tv-shows' ||
      (pathNameArray[1] === 'movies' &&
        !pathNameArray[pathNameArray.length - 1].match(
          collectionAssetsForIndexFollow
        ))) &&
    pathNameArray[2] === 'collections';

  const noIndexNoFollowFlag = getNoIndexNoFollowFlag(pathName, contentVersion);

  const noIndexFlag = getNoIndexFlag(countryCode, pathName) || noIndexNoFollow;

  const displayLanguageForMeta = getDisplayLanguageForMeta(
    countryCode,
    displayLanguage,
    isBotRequest
  );
  const indexFollowFlag = isShowDetail || isMovieDetails;
  const noIndexFollowFlag = isShowsCollection;

  return (
    <Head>
      {appHostname &&
      !(appHostname === 'zee5.com' || appHostname === 'www.zee5.com') ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          {indexFollowFlag ? (
            <meta name="robots" content="index, follow" />
          ) : null}
          {noIndexFollowFlag ? (
            <meta name="robots" content="noindex, follow" />
          ) : null}
          {noIndexFlag ? <meta name="robots" content="noindex" /> : null}
          {noFollow && !noIndex ? (
            <meta name="robots" content="index, nofollow" />
          ) : null}
          {!noFollow && noIndex ? (
            <meta name="robots" content="noindex, follow" />
          ) : null}
          {noFollow && noIndex ? (
            <meta name="robots" content="noindex, nofollow" />
          ) : null}
          {noIndexNoFollowFlag ? (
            <meta name="robots" content="noindex, nofollow" />
          ) : null}
          {isBotRequest ? (
            <meta
              httpEquiv="content-language"
              content={displayLanguageForMeta}
            />
          ) : null}
        </>
      )}
    </Head>
  );
};

export default CommonSEOMeta;
